import React, { useState } from 'react';
import BookingModal from '../components/BookingModal';

export default function About() {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-ayurveda-green-dark to-ayurveda-green text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            About Kalyan Ayurveda
          </h1>
          <div className="mx-auto h-1 w-24 bg-ayurveda-gold rounded mb-4"></div>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto">
            A trusted pillar in the community's health for years, dedicated to holistic Ayurvedic wellness
          </p>
        </div>
      </section>

      {/* Clinic Overview */}
      <section className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-heading font-bold text-ayurveda-green-dark mb-4">
              Our Story
            </h2>
            <div className="h-1 w-20 bg-ayurveda-gold rounded mb-6"></div>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Kalyan Ayurveda stands as a beacon of traditional Ayurvedic healing in Charkhi Dadri, Haryana. 
                For years, we have been a trusted local Ayurvedic hospital led by experienced practitioners 
                who are deeply committed to the ancient science of Ayurveda.
              </p>
              <p>
                Our reputation in the community is built on a foundation of compassionate care, proven results, 
                and unwavering dedication to each patient's wellbeing. We believe that true health comes from 
                addressing the root causes of imbalance, not just treating symptoms.
              </p>
              <p>
                Every patient who walks through our doors receives personalized attention and a treatment plan 
                tailored to their unique constitution and health needs. This patient-centric approach has made 
                us a pillar of trust and healing in our community.
              </p>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-ayurveda-green opacity-10 rounded-3xl transform -rotate-3"></div>
            <img 
              src="/about-clinic.jpg" 
              alt="Kalyan Ayurveda Clinic" 
              className="relative rounded-3xl shadow-2xl object-cover w-full h-[400px]"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600"><rect fill="%23E8F5E9" width="800" height="600"/><text x="50%" y="50%" font-size="28" text-anchor="middle" fill="%234CAF50" font-family="sans-serif">Kalyan Ayurveda</text></svg>';
              }}
            />
          </div>
        </div>
      </section>

      {/* Ayurvedic Philosophy */}
      <section className="section-container bg-ayurveda-green-light">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-ayurveda-green-dark mb-4">
              Our Ayurvedic Philosophy
            </h2>
            <div className="mx-auto h-1 w-24 bg-ayurveda-gold rounded"></div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p className="text-lg">
                <span className="font-bold text-ayurveda-green-dark">Ayurveda</span> is an ancient Indian 
                system of medicine that has been practiced for thousands of years. The word itself means 
                "science of life" – combining <em>Ayur</em> (life) and <em>Veda</em> (knowledge or science).
              </p>
              
              <blockquote className="border-l-4 border-ayurveda-gold pl-6 py-2 my-6 italic text-ayurveda-green-dark bg-ayurveda-gold-light rounded-r-lg">
                "We follow the time-honored Ayurvedic approach, treating each patient holistically 
                to restore natural balance and vitality."
              </blockquote>

              <p>
                At the heart of Ayurveda is the principle of <strong>balance</strong> – between body, mind, 
                and spirit, and between the individual and the universe. Ayurvedic philosophy recognizes that 
                each person is unique, with their own constitution (prakriti) determined by three fundamental 
                energies called doshas: Vata, Pitta, and Kapha.
              </p>

              <div className="grid md:grid-cols-3 gap-6 my-8">
                <div className="bg-ayurveda-green-light p-6 rounded-xl">
                  <h3 className="font-bold text-ayurveda-green-dark mb-2">Body</h3>
                  <p className="text-sm text-gray-600">
                    Physical health through natural herbs, proper diet, and therapeutic treatments
                  </p>
                </div>
                <div className="bg-ayurveda-gold-light p-6 rounded-xl">
                  <h3 className="font-bold text-ayurveda-green-dark mb-2">Mind</h3>
                  <p className="text-sm text-gray-600">
                    Mental clarity and emotional balance through meditation and mindfulness
                  </p>
                </div>
                <div className="bg-ayurveda-brown-light p-6 rounded-xl">
                  <h3 className="font-bold text-ayurveda-green-dark mb-2">Spirit</h3>
                  <p className="text-sm text-gray-600">
                    Spiritual wellness through connection with nature and inner harmony
                  </p>
                </div>
              </div>

              <p>
                Health, in Ayurveda, is not merely the absence of disease but a state of complete physical, 
                mental, and spiritual well-being. Disease arises when there is an imbalance in the doshas or 
                when toxins (ama) accumulate in the body.
              </p>

              <p>
                Our approach at Kalyan Ayurveda embodies these principles. We use traditional diagnostic methods 
                including <strong>Nadi Parikshan (pulse diagnosis)</strong> to understand your unique constitution 
                and identify imbalances. Then we create personalized treatment plans combining herbal medicines, 
                therapeutic procedures, dietary guidance, and lifestyle recommendations.
              </p>

              <p className="text-lg font-semibold text-ayurveda-green-dark">
                The goal is not just to treat illness, but to promote optimal health, prevent disease, 
                and help you achieve your highest potential for wellbeing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="section-container">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-ayurveda-green-dark mb-4">
            Our Commitment to Patients
          </h2>
          <div className="mx-auto h-1 w-24 bg-ayurveda-gold rounded"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card p-6 text-center">
            <div className="w-16 h-16 bg-ayurveda-green-light rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ayurveda-green-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="font-bold text-ayurveda-green-dark mb-2">Personalized Care</h3>
            <p className="text-gray-600 text-sm">
              Every treatment plan is customized to your unique constitution and health needs
            </p>
          </div>

          <div className="card p-6 text-center">
            <div className="w-16 h-16 bg-ayurveda-green-light rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ayurveda-green-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h3 className="font-bold text-ayurveda-green-dark mb-2">Careful Diagnosis</h3>
            <p className="text-gray-600 text-sm">
              Traditional pulse diagnosis and thorough assessment to identify root causes
            </p>
          </div>

          <div className="card p-6 text-center">
            <div className="w-16 h-16 bg-ayurveda-green-light rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ayurveda-green-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h3 className="font-bold text-ayurveda-green-dark mb-2">Compassionate Service</h3>
            <p className="text-gray-600 text-sm">
              We treat every patient with kindness, respect, and genuine care
            </p>
          </div>

          <div className="card p-6 text-center">
            <div className="w-16 h-16 bg-ayurveda-green-light rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ayurveda-green-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="font-bold text-ayurveda-green-dark mb-2">Proven Results</h3>
            <p className="text-gray-600 text-sm">
              Time-tested treatments delivering lasting relief and improved wellbeing
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-container bg-gradient-to-r from-ayurveda-green to-ayurveda-green-dark text-white">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
            Experience the Kalyan Ayurveda Difference
          </h2>
          <p className="text-lg mb-8 text-gray-100">
            Join countless others who have found healing and balance through our holistic Ayurvedic approach.
          </p>
          <button 
            onClick={() => setIsBookingModalOpen(true)}
            className="inline-block bg-white text-ayurveda-green-dark hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition shadow-lg"
          >
            Schedule Your Consultation
          </button>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
}
